import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaShoppingCart,
  FaFlask,
  FaArrowLeft,
  FaTag,
  FaStar,
  FaClock,
  FaVial,
  FaBuilding,
  FaHeart,
  FaRegHeart,
} from "react-icons/fa";
import { useSelector, useDispatch } from "react-redux";
import { useCartActions } from "../../hooks/useCartActions";
import {
  fetchWishlist,
  addToWishlist,
  removeFromWishlist,
  addWishlistOptimistic,
  removeWishlistOptimistic,
} from "../../Redux/wishlistSlice";
import Coupon from "../Coupon/Coupon";

export default function TestDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { addWithReplaceCheck, removeItem, LoginPopupComponent } =
    useCartActions();

  const cartItems = useSelector((state) => state.cart.items || []);
  const wishlistItems = useSelector((state) => state.wishlist.items || []);
  const wishlistLoading = useSelector((state) => state.wishlist.loading);

  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL;

  // ================= WISHLIST =================
  const getUserId = () => {
    try {
      const userData = JSON.parse(localStorage.getItem("medarphaUser") || "{}");
      return userData?.userId || null;
    } catch {
      return null;
    }
  };

  const isInWishlist = (productId, categoryId = 2) => {
    if (!wishlistItems || !Array.isArray(wishlistItems)) return false;
    return wishlistItems.some(
      item => Number(item.productId) === Number(productId) && 
              Number(item.categoryId) === Number(categoryId)
    );
  };

  const loadWishlist = async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      await dispatch(fetchWishlist(userId)).unwrap();
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  };

  const toggleWishlist = async (e) => {
    e.stopPropagation();
    const userId = getUserId();
    if (!userId) {
      setShowLoginPopup(true);
      return;
    }

    const productId = Number(test.TestID || test.testID);
    const categoryId = 2;
    const isWished = isInWishlist(productId, categoryId);

    if (isWished) {
      dispatch(removeWishlistOptimistic({ productId, categoryId }));
    } else {
      dispatch(addWishlistOptimistic({ productId, categoryId }));
    }

    try {
      if (isWished) {
        await dispatch(removeFromWishlist({ userId, productId, categoryId })).unwrap();
      } else {
        await dispatch(addToWishlist({ userId, productId, categoryId })).unwrap();
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      if (isWished) {
        dispatch(addWishlistOptimistic({ productId, categoryId }));
      } else {
        dispatch(removeWishlistOptimistic({ productId, categoryId }));
      }
      alert('Failed to update wishlist. Please try again.');
    }
  };

  // ================= FETCH =================
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${API_BASE}MasterAPI/TestMasterByTestId?TestId=${id}`
        );
        if (Array.isArray(res.data) && res.data.length > 0) {
          setTest(res.data[0]);
          await loadWishlist();
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, API_BASE]);

  // ================= LOADING =================
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!test) {
    return (
      <div className="text-center py-10 text-gray-500">No Data Found</div>
    );
  }

  // ================= PRICES =================
  const testPrice = Number(test.testPrice || test.TestPrice) || 0;
  const sellingPrice = Number(test.sellingPrice || test.SellingPrice) || testPrice;
  const discount = testPrice - sellingPrice;
  const discountPercent = testPrice > 0 ? Math.round((discount / testPrice) * 100) : 0;

  const testId = Number(test.TestID || test.testID);
  const inCart = cartItems.some(
    (item) => Number(item.productId) === testId && Number(item.categoryId) === 2
  );
  const inWishlist = isInWishlist(testId, 2);

  const buildCartItem = () => ({
    productId: testId,
    categoryId: 2,
    name: test.testName || test.TestName,
    price: sellingPrice,
    discount,
    quantity: 1,
    storeId: test.labId || test.LabId || 0,
    originalPrice: testPrice,
  });

  const handleAdd = () => !inCart && addWithReplaceCheck(buildCartItem());
  const handleRemove = () => removeItem(buildCartItem());

  const isPopular = test.IsPopular === true || test.isPopular === true;
  const isFasting = test.IsFasting === true || test.isFasting === true;
  const labId = test.labId || test.LabId || 0;

  // Report Time
  const reportTime = test.ReportTime || test.reportTime;
  const reportTimeType = test.ReportTimeType || test.reportTimeType;
  let reportText = "";
  if (reportTime) {
    const unit = reportTimeType === 2 ? "days" : "hours";
    reportText = `⏱️ Reports in ${reportTime} ${unit}`;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-white border-b shadow-sm mt-4">
        <div className="max-w-7xl mx-auto px-3 md:px-5 py-2.5 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center hover:bg-red-200 transition"
          >
            <FaArrowLeft size={14} />
          </button>
          <h1 className="text-sm font-bold text-gray-800">Test Details</h1>
          <span className="text-xs text-gray-400 ml-auto">
            {test.testCode || test.TestCode}
          </span>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 md:px-5 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-3">
            {/* Test Card - Like PopularTests */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 relative">
              
              {/* Wishlist Button */}
              <button
                onClick={toggleWishlist}
                className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/80 backdrop-blur-sm shadow-md hover:bg-white transition-all duration-200"
                disabled={wishlistLoading}
              >
                {wishlistLoading ? (
                  <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  inWishlist ? (
                    <FaHeart size={16} className="text-red-500 fill-red-500" />
                  ) : (
                    <FaRegHeart size={16} className="text-gray-400 hover:text-red-500" />
                  )
                )}
              </button>

              {/* Badges */}
              <div className="flex flex-wrap gap-1.5 absolute top-3 left-3">
                {isPopular && (
                  <span className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-lg flex items-center gap-1">
                    <FaStar size={9} className="fill-white" /> POPULAR
                  </span>
                )}
                {discount > 0 && (
                  <span className="bg-gradient-to-r from-red-500 to-red-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-md">
                    {discountPercent}% OFF
                  </span>
                )}
                {isFasting && (
                  <span className="bg-blue-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-md flex items-center gap-1">
                    <FaClock size={9} /> Fasting
                  </span>
                )}
              </div>

              {/* Test Name */}
              <h2 className="text-xl font-bold mt-8 text-gray-800">
                {test.testName || test.TestName}
              </h2>

              {/* Lab Name */}
              {test.lab_name && (
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <FaBuilding size={12} /> {test.lab_name}
                </p>
              )}

              {/* Report Time Badge */}
              {reportText && (
                <div className="flex items-center mt-2">
                  <span className="text-[11px] font-semibold text-red-700 bg-red-50 px-3 py-1 rounded-full border border-red-200 shadow-sm">
                    {reportText}
                  </span>
                </div>
              )}

              {/* Tags */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {test.departmentName && (
                  <span className="text-[10px] bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-full">
                    {test.departmentName}
                  </span>
                )}
                {test.categoryName && (
                  <span className="text-[10px] bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-full">
                    {test.categoryName}
                  </span>
                )}
                {test.sampleName && (
                  <span className="text-[10px] bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-full">
                    {test.sampleName}
                  </span>
                )}
              </div>

              {/* Price Section OR Coupon */}
              <div className="mt-4 pt-3 border-t border-gray-100">
                {discount > 0 ? (
                  // DISCOUNT SECTION
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col">
                      <div className="text-2xl font-extrabold text-green-600">
                        ₹{sellingPrice}
                      </div>
                      {testPrice > 0 && (
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs line-through text-gray-400">
                            ₹{testPrice}
                          </span>
                          <span className="text-[11px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded-md">
                            Save ₹{discount}
                          </span>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => (inCart ? handleRemove() : handleAdd())}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-semibold border transition-all duration-200
                        ${inCart
                          ? "bg-red-50 text-red-600 border-red-500 hover:bg-red-100"
                          : "bg-blue-100 text-blue-600 border-blue-500 hover:bg-blue-200"
                        }`}
                    >
                      <FaShoppingCart size={10} />
                      {inCart ? "Remove" : "Add"}
                    </button>
                  </div>
                ) : (
                  // COUPON SECTION
                  <div className="flex flex-col gap-2">
                    {/* Price and Add to Cart in same row */}
                    <div className="flex justify-between items-center">
                      {/* Price */}
                      {sellingPrice > 0 && (
                        <div className="flex flex-col">
                          <div className="text-xl font-extrabold text-green-600">
                            ₹{sellingPrice}
                          </div>
                          {testPrice > 0 && testPrice !== sellingPrice && (
                            <span className="text-xs line-through text-gray-400">
                              ₹{testPrice}
                            </span>
                          )}
                          {testPrice === sellingPrice && testPrice > 0 && (
                            <span className="text-[9px] text-gray-400">No Discount</span>
                          )}
                        </div>
                      )}

                      {/* Add to Cart Button */}
                      <button
                        onClick={() => (inCart ? handleRemove() : handleAdd())}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-semibold border transition-all duration-200
                          ${inCart
                            ? "bg-red-50 text-red-600 border-red-500 hover:bg-red-100"
                            : "bg-blue-100 text-blue-600 border-blue-500 hover:bg-blue-200"
                          }`}
                      >
                        <FaShoppingCart size={10} />
                        {inCart ? "Remove" : "Add"}
                      </button>
                    </div>

                    {/* Coupon Component */}
                    <Coupon
                      labId={labId}
                      userId={getUserId()}
                      testPrice={testPrice}
                      sellingPrice={sellingPrice}
                      discount={discount}
                      showCopyButton={false}
                      showAddToCart={false}
                      isInCart={inCart}
                      onAddToCart={handleAdd}
                      onRemoveFromCart={handleRemove}
                      className="w-full"
                      loadingText="Loading coupon..."
                      noCouponText="No coupon available"
                      labelText="🎫 No Discount - Coupon"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            {(test.description || test.Description) && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <FaTag size={14} className="text-red-400" /> About Test
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {test.description || test.Description}
                </p>
              </div>
            )}

            {/* Test Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <FaVial size={14} className="text-red-400" /> Test Information
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Info label="Method" value={test.methodName || test.MethodName} />
                <Info label="Unit" value={test.unitName || test.UnitName} />
                <Info label="Format" value={test.formatName || test.FormatName} />
                <Info label="Synonym" value={test.synonymName || test.SynonymName} />
                <Info label="Symptom" value={test.symtomName || test.SymtomName} />
                <Info label="Fasting" value={isFasting ? "Required" : "Not Required"} />
                <Info label="Status" value={test.isActive || test.IsActive ? "Active" : "Inactive"} />
              </div>
            </div>
          </div>

          {/* Right Column - Book Test Summary */}
          <div className="lg:sticky lg:top-20 h-fit">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-3">
                <h3 className="flex items-center gap-2 text-base font-bold">
                  <FaTag size={14} /> Book Test
                </h3>
              </div>
              <div className="p-4">
                {/* Price Summary */}
                <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Final Price</p>
                  {sellingPrice > 0 ? (
                    <>
                      <div className="text-2xl font-bold text-red-600">₹{sellingPrice}</div>
                      {testPrice > sellingPrice && (
                        <div className="text-[10px] line-through text-gray-400">₹{testPrice}</div>
                      )}
                      {discount > 0 ? (
                        <div className="text-[10px] text-green-600 font-semibold mt-0.5">
                          Save ₹{discount} ({discountPercent}% OFF)
                        </div>
                      ) : (
                        <div className="text-[10px] text-gray-500 mt-0.5">No Discount Available</div>
                      )}
                    </>
                  ) : (
                    <div className="text-sm text-gray-500">Price not available</div>
                  )}
                </div>

                {/* 🔥 Coupon in Book Test Section */}
                {discount === 0 && sellingPrice === 0 && (
                  <div className="mt-3">
                    <Coupon
                      labId={labId}
                      userId={getUserId()}
                      testPrice={testPrice}
                      sellingPrice={sellingPrice}
                      discount={discount}
                      showCopyButton={false}
                      showAddToCart={false}
                      isInCart={inCart}
                      onAddToCart={handleAdd}
                      onRemoveFromCart={handleRemove}
                      className="w-full"
                      loadingText="Loading coupon..."
                      noCouponText="No coupon available"
                      labelText="🎫 Coupon Available"
                    />
                  </div>
                )}

                {/* Add to Cart Button */}
                <button
                  onClick={() => (inCart ? handleRemove() : handleAdd())}
                  className={`w-full mt-3 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 ${
                    inCart
                      ? "bg-red-50 text-red-600 border-2 border-red-500 hover:bg-red-100"
                      : "bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 shadow-md hover:shadow-lg"
                  }`}
                >
                  <FaShoppingCart size={16} />
                  {inCart ? "Remove from Cart" : "Add to Cart"}
                </button>

                {/* Quick Info */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="grid grid-cols-2 gap-2">
                    {reportText && (
                      <div className="text-[9px] text-gray-500">
                        <span className="block font-medium text-gray-700">Report Time</span>
                        {reportTime} {reportTimeType === 2 ? "days" : "hours"}
                      </div>
                    )}
                    {isFasting && (
                      <div className="text-[9px] text-gray-500">
                        <span className="block font-medium text-gray-700">Fasting</span>
                        Required
                      </div>
                    )}
                    {test.sampleName && (
                      <div className="text-[9px] text-gray-500">
                        <span className="block font-medium text-gray-700">Sample</span>
                        {test.sampleName}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {LoginPopupComponent}
    </div>
  );
}

// ================= INFO COMPONENT =================
function Info({ label, value }) {
  return (
    <div className="bg-gray-50 p-2 rounded-lg">
      <p className="text-[9px] text-gray-500">{label}</p>
      <p className="text-[11px] font-medium text-gray-800 truncate">
        {value || "-"}
      </p>
    </div>
  );
}